import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Camera, Check, AlertTriangle } from 'lucide-react';
import { collectDiagnostics, submitFeedback } from '../api/feedback';
import ElementSnip from './ElementSnip';

// Feedback & bug report modal (handoff 14 §B). Opened from the account menu.
// Captures a short report + AUTO-DIAGNOSTICS (browser/OS/viewport/screen+DPR/
// theme/tz/language/page) + screenshots (upload / paste / drag-drop / a
// BugHerd-style element snip), then POSTs to the app-native feedback store
// (api/feedback.js). Ported from the prototype shell.js openFeedbackModal;
// controls carry their own colours so there is no <button>-theme bleed.

const MAX_FILES = 6;
const MAX_EACH = 8 * 1024 * 1024; // 8 MB

const KINDS = [
  { v: 'bug', label: "Bug \u00b7 something's broken" },
  { v: 'idea', label: 'Idea \u00b7 a suggestion' },
  { v: 'question', label: 'Question' },
  { v: 'other', label: 'Other' },
];
const SEVERITIES = [
  { v: 'low', label: 'Low' },
  { v: 'medium', label: 'Medium' },
  { v: 'high', label: 'High' },
];

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

// Ask for a stream of THE CURRENT TAB. preferCurrentTab makes Chrome offer this
// tab directly (no tab list to hunt through, which is what made the old flow
// awkward). Falls back to the standard browser-surface picker where that hint
// is unsupported. Returns null on cancel / unavailable.
async function requestTabStream() {
  const md = navigator.mediaDevices;
  if (!md || !md.getDisplayMedia) return { stream: null, reason: 'unsupported' };
  try {
    const stream = await md.getDisplayMedia({ preferCurrentTab: true, video: true, audio: false });
    return { stream, reason: null };
  } catch (err) {
    if (err && err.name === 'NotAllowedError') return { stream: null, reason: 'cancelled' };
    try {
      const stream = await md.getDisplayMedia({ video: { displaySurface: 'browser' }, audio: false });
      return { stream, reason: null };
    } catch (err2) {
      if (err2 && err2.name === 'NotAllowedError') return { stream: null, reason: 'cancelled' };
      return { stream: null, reason: 'error' };
    }
  }
}

const LABEL = 'block mb-1 font-mono text-[10.5px] font-bold tracking-wider uppercase text-slate-500';
const FIELD = 'block w-full box-border mt-1 text-[13px] leading-snug text-eddp-dark bg-white border border-slate-300 rounded-lg px-2.5 py-2 outline-none focus:border-eddp-primary';

export default function FeedbackModal({ open, onClose, page }) {
  const [kind, setKind] = useState('bug');
  const [severity, setSeverity] = useState('medium');
  const [summary, setSummary] = useState('');
  const [detail, setDetail] = useState('');
  const [expected, setExpected] = useState('');
  const [atts, setAtts] = useState([]);
  const [msg, setMsg] = useState(null); // { text, error }
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null); // { count }
  const [dragActive, setDragActive] = useState(false);
  const [snipStream, setSnipStream] = useState(null); // active element-snip stream

  const fileRef = useRef(null);
  const summaryRef = useRef(null);

  // Diagnostics snapshot taken when the modal opens.
  const diag = useMemo(() => (open ? collectDiagnostics(page) : null), [open, page]);

  // Reset all fields whenever the modal (re)opens.
  useEffect(() => {
    if (!open) return;
    setKind('bug'); setSeverity('medium'); setSummary(''); setDetail(''); setExpected('');
    setAtts([]); setMsg(null); setSending(false); setDone(null); setDragActive(false);
    const t = setTimeout(() => summaryRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  const close = useCallback(() => { if (!sending) onClose?.(); }, [sending, onClose]);

  // Escape to close (dormant while the element snipper owns the screen — it has
  // its own Esc that cancels the snip rather than the whole modal).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !snipStream) close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close, snipStream]);

  const addFiles = useCallback((list) => {
    const files = Array.prototype.slice.call(list || []);
    files.forEach((f) => {
      setAtts((prev) => {
        if (prev.length >= MAX_FILES) { setMsg({ text: `Up to ${MAX_FILES} attachments.`, error: true }); return prev; }
        return prev;
      });
      if (f.size > MAX_EACH) { setMsg({ text: `${f.name} is too large (max 8 MB).`, error: true }); return; }
      const reader = new FileReader();
      reader.onload = () => {
        setAtts((prev) => {
          if (prev.length >= MAX_FILES) return prev;
          setMsg(null);
          return [...prev, { name: f.name || 'image.png', type: f.type || 'application/octet-stream', size: f.size, dataUrl: reader.result }];
        });
      };
      reader.readAsDataURL(f);
    });
  }, []);

  const removeAt = useCallback((i) => setAtts((prev) => prev.filter((_, idx) => idx !== i)), []);

  // Start the BugHerd-style element snip: grab the current tab, then hand the
  // stream to <ElementSnip>, which hides this modal, outlines the element under
  // the cursor, and crops the click target (or the whole page on F).
  const captureElement = useCallback(async () => {
    const { stream, reason } = await requestTabStream();
    if (!stream) {
      if (reason === 'cancelled') return; // user backed out of the browser prompt
      setMsg({ text: 'Screen capture is not available here \u2013 use your OS snip tool, then paste (Ctrl/\u2318+V) or choose the file.', error: true });
      return;
    }
    setMsg(null);
    setSnipStream(stream);
  }, []);

  const onSnipCapture = useCallback((file) => {
    setSnipStream(null);
    if (file) addFiles([file]);
  }, [addFiles]);

  const onSnipCancel = useCallback(() => setSnipStream(null), []);

  // Safety net: if the modal unmounts mid-snip, stop the shared tab.
  useEffect(() => () => {
    if (snipStream) { try { snipStream.getTracks().forEach((t) => t.stop()); } catch (_) { /* gone */ } }
  }, [snipStream]);

  const onPaste = useCallback((e) => {
    const items = (e.clipboardData && e.clipboardData.items) || [];
    const imgs = [];
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].type && items[i].type.indexOf('image') === 0) {
        const f = items[i].getAsFile();
        if (f) imgs.push(f);
      }
    }
    if (imgs.length) { e.preventDefault(); addFiles(imgs); }
  }, [addFiles]);

  const send = useCallback(async () => {
    const s = summary.trim();
    if (!s) { setMsg({ text: 'Please add a one-line summary.', error: true }); summaryRef.current?.focus(); return; }
    setSending(true); setMsg(null);
    try {
      await submitFeedback({ kind, severity, summary: s, detail, expected, page: diag?.page || page, diagnostics: diag, attachments: atts });
      setDone({ count: atts.length });
    } catch (err) {
      setMsg({ text: err?.message || 'Could not send your report. Please try again.', error: true });
      setSending(false);
    }
  }, [summary, kind, severity, detail, expected, diag, page, atts]);

  if (!open) return null;

  const diagChips = diag ? [
    ['Browser', diag.browser], ['OS', diag.os], ['Viewport', diag.viewport],
    ['Screen', `${diag.screen} @${diag.dpr}x`], ['Theme', diag.colorScheme],
    ['Time zone', diag.timezone], ['Language', diag.language], ['Page', diag.page],
  ] : [];

  return (
    <>
    {/* While snipping, the whole modal (backdrop included) is hidden so it is
        never in the shot; the transparent picker lives in its own body portal. */}
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-[rgba(8,28,26,0.55)] backdrop-blur-sm p-[4vh_4vw]"
      style={snipStream ? { visibility: 'hidden' } : undefined}
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
      onPaste={onPaste}
      role="dialog"
      aria-modal="true"
      aria-label="Feedback and bug report"
    >
      <div className="w-[min(560px,100%)] max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
          <span className="text-[15px] font-extrabold text-eddp-dark">Feedback &amp; bug report</span>
          <button type="button" onClick={close} aria-label="Close" className="text-slate-400 hover:text-slate-600 text-lg leading-none">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="px-6 py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mx-auto mb-3">
              <Check size={24} />
            </div>
            <h4 className="text-base font-bold text-emerald-700 mb-1">Thanks &ndash; report logged.</h4>
            <p className="text-[13px] text-slate-500 mb-5">
              Diagnostics{done.count ? ` and ${done.count} attachment${done.count > 1 ? 's' : ''}` : ''} were included.
            </p>
            <button type="button" onClick={onClose}
              className="inline-block text-[13px] font-semibold rounded-lg px-4 py-2 bg-emerald-700 text-white hover:bg-emerald-800">
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white px-[18px] py-4 overflow-y-auto">
              <p className="text-[12.5px] text-slate-500 mt-0 mb-3 leading-relaxed">
                Tell us what&apos;s not right, or what would make this better. Your report is logged for the team,
                with everything below attached so we can reproduce it.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <label className={LABEL}>Type
                  <select value={kind} onChange={(e) => setKind(e.target.value)} className={FIELD}>
                    {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
                  </select>
                </label>
                <label className={LABEL}>Severity
                  <select value={severity} onChange={(e) => setSeverity(e.target.value)} className={FIELD}>
                    {SEVERITIES.map((sv) => <option key={sv.v} value={sv.v}>{sv.label}</option>)}
                  </select>
                </label>
              </div>

              <label className={`${LABEL} mt-3`}>Summary <span className="text-amber-600 font-bold">required</span>
                <input ref={summaryRef} type="text" maxLength={240} value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder={'One line \u2013 what happened, in short'} className={FIELD} />
              </label>

              <label className={`${LABEL} mt-3`}>What happened / details
                <textarea rows={4} value={detail} onChange={(e) => setDetail(e.target.value)}
                  placeholder="Steps, what you saw, anything that helps us reproduce it"
                  className={`${FIELD} resize-y`} />
              </label>

              <label className={`${LABEL} mt-3`}>What you expected
                <textarea rows={2} value={expected} onChange={(e) => setExpected(e.target.value)}
                  placeholder={'Optional \u2013 what you expected instead'}
                  className={`${FIELD} resize-y`} />
              </label>

              {/* Attachments */}
              <div className={`${LABEL} mt-3`}>Screenshots &amp; attachments
                <div
                  tabIndex={0}
                  onClick={() => fileRef.current?.click()}
                  onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                  onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer?.files) addFiles(e.dataTransfer.files); }}
                  className={`mt-1.5 border-[1.5px] border-dashed rounded-[10px] px-3.5 py-3 text-center normal-case tracking-normal text-[12.5px] font-normal cursor-pointer transition-colors ${dragActive ? 'border-emerald-700 bg-emerald-50' : 'border-slate-300 bg-slate-50'} text-slate-500`}
                >
                  <b className="text-eddp-dark">Drop images here</b>, paste (Ctrl/&#8984;+V),{' '}
                  <span role="button" className="text-emerald-700 font-semibold underline"
                    onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}>choose files</span>, or{' '}
                  <span role="button" className="text-cyan-600 font-semibold underline inline-flex items-center gap-1"
                    onClick={(e) => { e.stopPropagation(); captureElement(); }}><Camera size={12} /> capture an element</span>
                </div>
                {atts.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {atts.map((a, i) => (
                      <div key={`${a.name}-${i}`} title={`${a.name} \u00b7 ${fmtSize(a.size)}`} className="relative w-[84px]">
                        {/^image\//.test(a.type) ? (
                          <img src={a.dataUrl} alt="" className="w-[84px] h-[60px] object-cover rounded-[7px] border border-slate-300 block" />
                        ) : (
                          <span className="w-[84px] h-[60px] rounded-[7px] border border-slate-300 bg-white flex items-center justify-center text-2xl text-slate-400">&#9646;</span>
                        )}
                        <button type="button" aria-label={`Remove ${a.name}`} onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-eddp-dark text-white text-[11px] flex items-center justify-center shadow cursor-pointer">
                          <X size={11} />
                        </button>
                        <span className="block mt-0.5 text-[9.5px] text-slate-500 truncate normal-case font-normal tracking-normal">{a.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*,.pdf,.txt,.log,.json,.csv" multiple hidden
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
              </div>

              {/* Auto-diagnostics (read-only) */}
              <div className="mt-3 mb-3 border border-slate-200 rounded-[10px] bg-slate-50 px-3 py-2.5">
                <div className="font-mono text-[10px] font-bold tracking-wider uppercase text-slate-500 mb-1.5">
                  Automatically included <span className="font-medium normal-case tracking-normal text-slate-400">so we can reproduce it</span>
                </div>
                <div className="grid grid-cols-2 gap-x-3.5 gap-y-1">
                  {diagChips.filter(([, v]) => v).map(([l, v]) => (
                    <div key={l} className="flex justify-between gap-2 text-[11.5px] border-b border-dotted border-slate-200 py-0.5">
                      <span className="text-slate-500 font-semibold">{l}</span>
                      <span className="text-eddp-dark text-right truncate">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-[18px] py-3 border-t border-slate-200">
              <span aria-live="polite" className={`text-[12px] inline-flex items-center gap-1 ${msg?.error ? 'text-red-600' : 'text-slate-500'}`}>
                {msg?.error && <AlertTriangle size={13} />} {msg?.text || ''}
              </span>
              <span className="flex gap-2">
                <button type="button" onClick={close} disabled={sending}
                  className="text-[13px] font-semibold rounded-lg px-4 py-2 border border-slate-300 text-eddp-dark hover:bg-slate-50 disabled:opacity-50">
                  Cancel
                </button>
                <button type="button" onClick={send} disabled={sending}
                  className="text-[13px] font-semibold rounded-lg px-4 py-2 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60">
                  {sending ? 'Sending\u2026' : 'Send report'}
                </button>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
    {snipStream && (
      <ElementSnip stream={snipStream} onCapture={onSnipCapture} onCancel={onSnipCancel} />
    )}
    </>
  );
}
